import { useEffect, useState } from 'react';
import SideBar from '../../components/global/SideBar';
import './index.css';
import { Loader, QuestionAndAnswer } from '@beholos/holoscommon';
import ProgramCoachesCarousel from '../../components/programs/ProgramCoachesCarousel';
import ProgramCard from '../../components/programs/ProgramCard';
import { Banners } from '../../components/programs/Banners';
import type { ProgramInterface } from '../../types/program.interface';
import { fetchPrograms, fetchUserPrograms } from '../../services/program.service';
import type { UserProgram } from '../../types/userProgram.interface';

export default function Programs() {
  const [programs, setPrograms] = useState<ProgramInterface[]>([]);
  const [userPrograms, setUserPrograms] = useState<UserProgram[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    document.title = 'Programas y Retos | Holos';
    fetchPrograms().then(setPrograms).finally(() => setLoading(false));
    fetchUserPrograms().then((data) => {
      if (data) {
        setUserPrograms(data);
      }
    }).catch(error => {
      console.error('Error fetching user programs:', error);
    });
  }, []);

  return (
    <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
      <div className="hidden md:block md:w-80 lg:w-96 h-full overflow-y-auto">
        <SideBar />
      </div>

      <div className="flex-1 h-full overflow-y-auto bg-gray-100">
        <div>
          <Banners />

          <section className="flex flex-col lg:flex-row items-center bg-white pt-20 px-6 lg:px-16">
            <div className="w-full lg:w-1/2 mb-8 lg:mb-0">
              <img
                src="https://image.beholos.com/filters:quality(80)/img/fitness-pass-v2/life/personal-training/program-results.jpg"
                alt="Program Results"
                className="w-full h-auto object-cover rounded"
              />
            </div>

            <div className="w-full lg:w-1/2 text-center lg:px-20">
              <h2 className="text-4xl font-light italic mb-4 leading-snug font-juana">
                LOS RESULTADOS EMPIEZAN AQUÍ
              </h2>
              <p className="text-gray-700 text-lg">
                Empieza un programa de acuerdo a tu objetivo, nivel y duración. En cada programa encontrarás materiales de soporte que te ayudarán a completarlo.
              </p>
            </div>
          </section>

          <section className='pt-20 bg-white'>
            <a id="lista" />
            <div className='font-juana text-5xl font-bold text-center pb-5'>Nuestros programas y retos</div>
            <div className='font-juana text-lg text-center pb-10'>Escoge el programa o reto de acuerdo a tu objetivo, nivel y duración. Da el primer paso a una vida llena de bienestar.</div>
            <div className="max-w-7xl mx-auto px-4 py-5">
              {loading ? (
                <Loader />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {programs.map((program: ProgramInterface) => {
                    const userProgram = userPrograms.find(userProgram => userProgram.program._id === program._id);
                    return (
                      <ProgramCard
                        program={{
                          ...program,
                          ...(userProgram?.programCompleted && { programCompleted: true }),
                          ...(userProgram && !userProgram?.programCompleted && { inProgress: true }),
                        }} />
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section className="pb-20 pt-12 bg-white">
            <div className='flex flex-col lg:flex-row items-center px-6 lg:px-16'>
              <div className="w-full lg:w-1/3 mb-8 lg:mb-0">
                <h2 className="text-4xl font-light italic mb-4 leading-snug font-juana">
                  Conoce a nuestros Instructores y coaches
                </h2>
              </div>

              <div className="w-full lg:w-2/3 text-left px-20">
                <p className="text-gray-700 text-lg">
                  Nuestros coaches y entrenadores son los mejores del medio y han ayudado a miles a obtener resultados que nunca imaginaron posible. Conóce más dando clic en su perfil.
                </p>
              </div>
            </div>

            <ProgramCoachesCarousel />
          </section>

          <section className="flex flex-col lg:flex-row items-center border-b border-grey-200 py-12 px-6 lg:px-16">
            <div className="w-full lg:w-1/3 mb-8 lg:mb-0">
              <img
                src="https://image.beholos.com/filters:quality(80)/img/fitness-pass-v2/life/personal-training/habitos-saludables.jpg"
                alt="Program Results"
                className="w-full h-auto object-cover rounded"
              />
            </div>

            <div className="w-full lg:w-2/3 text-left px-20">
              <h2 className="text-4xl font-light italic mb-4 leading-snug font-juana">
                Construye hábitos saludables
              </h2>
              <p className="text-gray-700 text-lg">
                El éxito para llegar a tus metas no solo suceden en el gym. La alimentación consciente es básica para alcanzar tus objetivos. Descubre cientos de recetas saludables en el blog y/o agenda una cita con nuestros nutricionistas colegiados para recibir un plan de alimentos personalizado.
              </p>
              <button className="px-5 py-4 border-2 border-black rounded-md text-lg font-semibold mx-auto block mt-4 cursor-pointer">
                VER NUTRICIONISTAS Y RECETAS
              </button>
            </div>
          </section>

          <section className='py-20'>
            <div className="mx-auto flex flex-col flex-wrap">
              <div className='text-4xl font-juana text-center my-6'>Estamos para ayudarte a triunfar</div>
              <div className="flex flex-row flex-wrap justify-center lg:justify-between w-full">
                <div className="flex flex-col items-center w-full md:w-1/3 mb-8 md:mb-0 px-4">
                  <img
                    src="https://cache.beholos.com/img/fitness-pass-v2/life/personal-training/icon-1.svg"
                    alt="24/7"
                    width={70}
                    height={70}
                  />
                  <div className="flex flex-col items-center mt-4">
                    <div className="text-center text-base px-7 text-lg text-black font-medium">
                      Acceso 24/7 ilimitado a todos nuestros programas online
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center w-full md:w-1/3 mb-8 md:mb-0 px-4">
                  <img
                    src="https://cache.beholos.com/img/fitness-pass-v2/life/personal-training/icon-3.svg"
                    alt="Expertos en bienestar"
                    width={70}
                    height={80}
                  />
                  <div className="flex flex-col items-center mt-4">
                    <div className="text-center text-base px-7 text-lg text-black font-medium">
                      Los mejores consejos de expertos en bienestar
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center w-full md:w-1/3 mb-8 md:mb-0 px-4">
                  <img
                    src="https://cache.beholos.com/img/fitness-pass-v2/life/personal-training/icon-4.svg"
                    alt="Motivación constante"
                    width={70}
                    height={55}
                  />
                  <div className="flex flex-col items-center mt-4">
                    <div className="text-center text-base px-7 text-lg text-black font-medium">
                      Motivación constante y calendarios de progreso.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="flex flex-col lg:flex-row items-center bg-white py-25 px-6 lg:px-16">
            <div className="w-full lg:w-1/3 mb-8 lg:mb-0">
              <img
                src="https://cache.beholos.com/img/fitness-pass-v2/global/support-icon.svg"
                alt="Pregunta"
                className="w-15 h-auto object-cover rounded"
              />
              <h2 className="text-4xl font-light italic mb-4 leading-snug font-juana">
                ¿Tienes dudas?
              </h2>
              <p className="text-gray-700 text-lg">
                Aquí te dejamos las respuestas a esas preguntas más frecuentes.
              </p>
            </div>

            <div className="w-full lg:w-2/3 text-left px-20">
              <div className='py-2'>
                <QuestionAndAnswer title='¿Cuánto cuesta el acceso a los programas?'>
                  <p>
                    Para acceder a los programas debes contar con un plan activa. Los programas son una excelente opción para empezar. Incluyen un guía diseñada por expertos para ayudarte a cumplir tus objetivos de una manera sencilla.
                  </p>
                </QuestionAndAnswer>
              </div>
              <div className='py-2'>
                <QuestionAndAnswer title='¿Cómo empiezo un programa?'>
                  <p>
                    Inicia sesión, haz clic en programas y elige el que más te guste. Da clic en "Empezar Programa". Hay programas con una duración desde 3 días hasta 8 semanas. Además, podrás descargar un manual inicial, un calendario y una guía nutricional en programas seleccionados.
                  </p>
                </QuestionAndAnswer>
              </div>
              <div className='py-2'>
                <QuestionAndAnswer title='¿Puedo hacer varios programas a la vez en un mes?'>
                  <p>
                    Sí, puedes hacer varios programas a la vez. Todos los meses añadimos nuevos programas. Puedes complementarlo con Videos On-Demand adicionales o clases grupales (online o presenciales) según el tipo de plan que tengas.
                  </p>
                </QuestionAndAnswer>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
